import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { Plus, Pencil, Trash2, Mail, ShieldCheck, CheckCircle2, Clock, Upload, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import LLMDiagnosticPanel from './LLMDiagnosticPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// OrganizationsTab — B2B subscription units management
const OrganizationsTab = () => {
  const [orgs, setOrgs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDomains, setNewDomains] = useState('');
  const [editing, setEditing] = useState(null);
  const [editDomains, setEditDomains] = useState('');
  const [editName, setEditName] = useState('');
  const [showMembers, setShowMembers] = useState(null);
  const [members, setMembers] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [orgsRes, plansRes] = await Promise.all([
        axios.get(`${API}/admin/organizations`, { withCredentials: true }),
        axios.get(`${API}/organizations/plans`),
      ]);
      setOrgs(orgsRes.data || []);
      setPlans(plansRes.data?.plans || []);
    } catch (e) {
      toast.error('Erreur chargement organisations');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!newName.trim()) return toast.error('Nom requis');
    const domains = newDomains.split(',').map(d => d.trim()).filter(Boolean);
    try {
      await axios.post(`${API}/admin/organizations`, { name: newName.trim(), allowed_domains: domains }, { withCredentials: true });
      toast.success('Organisation créée');
      setNewName(''); setNewDomains(''); setShowCreate(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur création');
    }
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    const domains = editDomains.split(',').map(d => d.trim()).filter(Boolean);
    try {
      await axios.patch(`${API}/admin/organizations/${editing.organization_id}`,
        { name: editName.trim(), allowed_domains: domains },
        { withCredentials: true });
      toast.success('Mis à jour');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur mise à jour');
    }
  };

  const handleDelete = async (org) => {
    if (!window.confirm(`Supprimer "${org.name}" ? Les membres seront détachés.`)) return;
    try {
      await axios.delete(`${API}/admin/organizations/${org.organization_id}`, { withCredentials: true });
      toast.success('Organisation supprimée');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression');
    }
  };

  const handleShowMembers = async (org) => {
    setShowMembers(org);
    try {
      const r = await axios.get(`${API}/admin/organizations/${org.organization_id}/members`, { withCredentials: true });
      setMembers(r.data || []);
    } catch (e) {
      setMembers([]);
    }
  };

  const statusBadge = (status) => {
    const map = {
      active: { c: 'bg-emerald-100 text-emerald-700 border-emerald-200', l: 'Actif' },
      inactive: { c: 'bg-gray-100 text-gray-600 border-gray-200', l: 'Inactif' },
      past_due: { c: 'bg-amber-100 text-amber-700 border-amber-200', l: 'Impayé' },
      canceling: { c: 'bg-orange-100 text-orange-700 border-orange-200', l: 'Annulation prog.' },
      canceled: { c: 'bg-red-100 text-red-700 border-red-200', l: 'Annulé' },
    };
    const m = map[status] || map.inactive;
    return <span className={`inline-flex items-center text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full border ${m.c}`}>{m.l}</span>;
  };

  return (
    <div data-testid="organizations-tab">
      <div className="flex items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Organisations B2B</h2>
          <p className="text-xs text-gray-500 mt-1">
            Gérer les organisations clientes : abonnements Stripe, domaines email autorisés et auto-onboarding des collaborateurs.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="org-create-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle organisation
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        {plans.map(p => (
          <div key={p.key} className={`p-4 rounded-lg border ${p.available ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
            <div className="flex items-center justify-between mb-1">
              <h4 className="font-semibold text-gray-900">{p.name}</h4>
              <span className="text-xs text-gray-500">{p.price_eur_monthly}€/mois</span>
            </div>
            <p className="text-xs text-gray-600">{p.tagline}</p>
            <p className="text-[11px] text-gray-400 mt-2">
              {p.available
                ? <><CheckCircle2 className="inline w-3 h-3 text-emerald-500 mr-1" /> Stripe price configuré</>
                : <><Clock className="inline w-3 h-3 text-amber-500 mr-1" /> Définir <code className="text-[10px] bg-white px-1 rounded">STRIPE_PRICE_{p.key.toUpperCase()}</code></>}
            </p>
          </div>
        ))}
      </div>

      <LLMDiagnosticPanel />

      {loading ? (
        <div className="text-sm text-gray-500 py-8 text-center">Chargement…</div>
      ) : orgs.length === 0 ? (
        <div className="text-sm text-gray-500 py-12 text-center border border-dashed border-gray-200 rounded-lg" data-testid="org-empty-state">
          Aucune organisation. Créez la première pour activer l'auto-onboarding B2B.
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left">Nom</th>
                <th className="px-4 py-2 text-left">Domaines</th>
                <th className="px-4 py-2 text-left">Plan</th>
                <th className="px-4 py-2 text-center">Quota</th>
                <th className="px-4 py-2 text-center">Membres</th>
                <th className="px-4 py-2 text-center">Statut</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orgs.map(o => (
                <tr key={o.organization_id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`org-row-${o.organization_id}`}>
                  <td className="px-4 py-3 font-medium text-gray-900">{o.name}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    {(o.allowed_domains || []).map(d => (
                      <span key={d} className="inline-flex items-center gap-1 mr-1.5 mb-1 px-1.5 py-0.5 bg-gray-100 rounded text-[10px]">
                        <Mail className="w-2.5 h-2.5" />@{d}
                      </span>
                    ))}
                  </td>
                  <td className="px-4 py-3 text-xs">{o.plan_key || <span className="text-gray-400">—</span>}</td>
                  <td className="px-4 py-3 text-center text-xs text-gray-600">
                    {o.subscription_status === 'active'
                      ? `${o.monthly_used_tokens || 0} / ${o.monthly_quota_tokens || 0}`
                      : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => handleShowMembers(o)} className="text-blue-600 hover:underline text-xs font-medium" data-testid={`org-members-${o.organization_id}`}>
                      {o.members_count} membre{o.members_count > 1 ? 's' : ''}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">{statusBadge(o.subscription_status)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => { setEditing(o); setEditName(o.name); setEditDomains((o.allowed_domains || []).join(', ')); }}
                        className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                        data-testid={`org-edit-${o.organization_id}`}
                      ><Pencil className="w-3.5 h-3.5" /></button>
                      <button
                        onClick={() => handleDelete(o)}
                        className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded"
                        data-testid={`org-delete-${o.organization_id}`}
                      ><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle organisation B2B</DialogTitle>
            <DialogDescription>Définissez le nom et les domaines email qui rejoindront automatiquement cette organisation.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-gray-500">Nom</label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Promoteur ACME" data-testid="org-create-name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-gray-500">Domaines email autorisés</label>
              <Input value={newDomains} onChange={(e) => setNewDomains(e.target.value)} placeholder="acme.fr, agence-acme.com" data-testid="org-create-domains" />
              <p className="text-[10px] text-gray-400 mt-1">Séparés par des virgules. Sans le @. Tout utilisateur Google Direct avec un email de ces domaines sera automatiquement attaché.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
            <Button onClick={handleCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="org-create-submit">Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier l'organisation</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-gray-500">Nom</label>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-gray-500">Domaines</label>
              <Input value={editDomains} onChange={(e) => setEditDomains(e.target.value)} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-gray-500">Logo brochure (fallback)</label>
              <div className="mt-1 flex items-center gap-3 p-2 rounded border bg-gray-50">
                {editing?.sales_brochure_logo_url ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/file-storage/${encodeURIComponent(editing.sales_brochure_logo_url)}`}
                    alt="logo"
                    className="w-16 h-16 object-contain bg-white border rounded"
                  />
                ) : (
                  <div className="w-16 h-16 rounded border bg-white flex items-center justify-center">
                    <ImageIcon className="w-6 h-6 text-gray-300" />
                  </div>
                )}
                <div className="flex-1">
                  <label className="cursor-pointer inline-block">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        if (f.size > 5 * 1024 * 1024) {
                          toast.error('Max 5 Mo');
                          return;
                        }
                        const fd = new FormData();
                        fd.append('file', f);
                        try {
                          const r = await axios.post(
                            `${API}/admin/organizations/${editing.organization_id}/sales-brochure-logo`,
                            fd,
                            { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
                          );
                          setEditing((p) => ({ ...p, sales_brochure_logo_url: r.data.logo_url }));
                          setOrgs((prev) => prev.map((o) => (o.organization_id === editing.organization_id ? { ...o, sales_brochure_logo_url: r.data.logo_url } : o)));
                          toast.success('Logo uploadé');
                        } catch {
                          toast.error('Erreur upload');
                        }
                      }}
                      data-testid="org-logo-upload-input"
                    />
                    <Button type="button" variant="outline" size="sm" asChild data-testid="org-logo-upload-btn">
                      <span>
                        <Upload className="w-4 h-4 mr-1" />
                        {editing?.sales_brochure_logo_url ? 'Remplacer' : 'Uploader'}
                      </span>
                    </Button>
                  </label>
                  {editing?.sales_brochure_logo_url && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-2 text-red-500 hover:text-red-700"
                      data-testid="org-logo-delete-btn"
                      onClick={async () => {
                        try {
                          await axios.delete(`${API}/admin/organizations/${editing.organization_id}/sales-brochure-logo`, { withCredentials: true });
                          setEditing((p) => ({ ...p, sales_brochure_logo_url: null }));
                          setOrgs((prev) => prev.map((o) => (o.organization_id === editing.organization_id ? { ...o, sales_brochure_logo_url: null } : o)));
                          toast.success('Logo supprimé');
                        } catch {
                          toast.error('Erreur');
                        }
                      }}
                    >
                      Supprimer
                    </Button>
                  )}
                  <p className="text-[10px] text-gray-400 mt-1">PNG/JPG max 5 Mo · fallback pour tous les projets sans logo custom</p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
            <Button onClick={handleSaveEdit} className="bg-blue-600 hover:bg-blue-700" data-testid="org-edit-save">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!showMembers} onOpenChange={(o) => !o && setShowMembers(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Membres — {showMembers?.name}</DialogTitle>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto" data-testid="org-members-list">
            {members.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">Aucun membre attaché.</p>
            ) : members.map(m => (
              <div key={m.user_id} className="flex items-center justify-between border-b border-gray-100 py-2">
                <div>
                  <p className="font-medium text-sm text-gray-900">{m.name}</p>
                  <p className="text-xs text-gray-500">{m.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  {m.is_admin && <ShieldCheck className="w-3.5 h-3.5 text-blue-600" title="Admin" />}
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${
                    m.status === 'active' ? 'bg-emerald-100 text-emerald-700' : m.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
                  }`}>{m.status}</span>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrganizationsTab;
